/**
 * Cloudinary Image Optimization Utility
 * Transforms Cloudinary URLs to add optimization parameters
 */

export interface ImageTransformOptions {
  width?: number;
  height?: number;
  quality?: 'auto' | number; // 'auto' or 1-100
  format?: 'auto' | 'webp' | 'jpg' | 'png' | 'avif';
  crop?: 'fill' | 'fit' | 'scale' | 'thumb' | 'limit' | 'pad';
  gravity?: 'auto' | 'face' | 'center' | 'north' | 'south' | 'east' | 'west';
  aspectRatio?: string; // e.g., "16:9", "1:1"
  dpr?: number; // Device pixel ratio (1, 2, 3, etc.)
  fetchFormat?: 'auto'; // Automatically deliver best format for the browser
  blur?: number; // Blur intensity (1-2000)
  sharpen?: number; // Sharpen intensity (1-2000)
}

export type ImagePreset = 
  | 'thumbnail'      // Small preview (150x150)
  | 'card'           // Card/grid view (400x300)
  | 'gallery'        // Gallery view (800x600)
  | 'detail'         // Detail page (1200x900)
  | 'full'           // Full quality
  | 'avatar'         // User avatar (100x100)
  | 'hero'           // Hero/banner (1920x1080)
  | 'og-image';      // Open Graph image (1200x630)

/**
 * Check if URL is a Cloudinary URL
 */
export const isCloudinaryUrl = (url: string): boolean => {
  return url.includes('cloudinary.com') || url.includes('res.cloudinary.com');
};

/**
 * Extract Cloudinary components from URL
 */
const parseCloudinaryUrl = (url: string): { base: string; path: string } | null => {
  // Match pattern: https://res.cloudinary.com/{cloud_name}/image/upload/{transformations}/{path}
  const match = url.match(/(https?:\/\/res\.cloudinary\.com\/[^\/]+\/image\/upload\/)(.+)/);
  
  if (!match) return null;
  
  const base = match[1];
  const fullPath = match[2];
  
  // Remove existing transformations (everything before version or public_id)
  const pathMatch = fullPath.match(/(?:v\d+\/)?(.+)/);
  const path = pathMatch ? pathMatch[1] : fullPath;
  
  return { base, path };
};

/**
 * Build transformation string from options
 */
const buildTransformString = (options: ImageTransformOptions): string => {
  const parts: string[] = [];
  
  if (options.width) parts.push(`w_${options.width}`);
  if (options.height) parts.push(`h_${options.height}`);
  if (options.crop) parts.push(`c_${options.crop}`);
  if (options.gravity) parts.push(`g_${options.gravity}`);
  if (options.aspectRatio) parts.push(`ar_${options.aspectRatio.replace(':', '_')}`);
  if (options.quality) parts.push(`q_${options.quality}`);
  if (options.format) parts.push(`f_${options.format}`);
  if (options.dpr) parts.push(`dpr_${options.dpr}`);
  if (options.fetchFormat) parts.push(`f_${options.fetchFormat}`);
  if (options.blur) parts.push(`e_blur:${options.blur}`);
  if (options.sharpen) parts.push(`e_sharpen:${options.sharpen}`);
  
  return parts.join(',');
};

/**
 * Get preset transformation options
 */
export const getPresetOptions = (preset: ImagePreset): ImageTransformOptions => {
  const presets: Record<ImagePreset, ImageTransformOptions> = {
    'thumbnail': {
      width: 150,
      height: 150,
      crop: 'thumb',
      gravity: 'auto',
      quality: 'auto',
      format: 'auto',
      dpr: 2,
    },
    'avatar': {
      width: 100,
      height: 100,
      crop: 'thumb',
      gravity: 'face',
      quality: 'auto',
      format: 'auto',
      dpr: 2,
    },
    'card': {
      width: 400,
      height: 300,
      crop: 'fill',
      gravity: 'auto',
      quality: 'auto',
      format: 'auto',
      dpr: 2,
    },
    'gallery': {
      width: 800,
      height: 600,
      crop: 'limit',
      quality: 'auto',
      format: 'auto',
      dpr: 2,
    },
    'detail': {
      width: 1200,
      height: 900,
      crop: 'limit',
      quality: 'auto',
      format: 'auto',
      dpr: 2,
    },
    'full': {
      quality: 'auto',
      format: 'auto',
    },
    'hero': {
      width: 1920,
      height: 1080,
      crop: 'fill',
      gravity: 'auto',
      quality: 'auto',
      format: 'auto',
    },
    'og-image': {
      width: 1200,
      height: 630,
      crop: 'fill',
      gravity: 'auto',
      quality: 85,
      format: 'jpg',
    },
  };
  
  return presets[preset];
};

/**
 * Transform a Cloudinary URL with optimizations
 */
export const optimizeImage = (
  url: string,
  options: ImageTransformOptions | ImagePreset
): string => {
  // If URL is not from Cloudinary, return as-is
  if (!isCloudinaryUrl(url)) {
    return url;
  }
  
  // Parse the URL
  const parsed = parseCloudinaryUrl(url);
  if (!parsed) {
    return url; // Return original if parsing fails
  }
  
  // Get options (handle preset)
  const transformOptions = typeof options === 'string' 
    ? getPresetOptions(options) 
    : options;
  
  // Build transformation string
  const transformString = buildTransformString(transformOptions);
  
  // Construct optimized URL
  if (transformString) {
    return `${parsed.base}${transformString}/${parsed.path}`;
  }
  
  return url;
};

/**
 * Generate multiple variants of an image
 */
export const generateImageVariants = (url: string): Record<ImagePreset, string> => {
  const presets: ImagePreset[] = ['thumbnail', 'card', 'gallery', 'detail', 'full'];
  
  return presets.reduce((acc, preset) => {
    acc[preset] = optimizeImage(url, preset);
    return acc;
  }, {} as Record<ImagePreset, string>);
};

/**
 * Generate responsive srcset for an image
 */
export const generateSrcSet = (url: string, widths: number[] = [400, 800, 1200, 1600]): string => {
  if (!isCloudinaryUrl(url)) {
    return '';
  }
  
  return widths
    .map(width => {
      const optimized = optimizeImage(url, {
        width,
        quality: 'auto',
        format: 'auto',
        crop: 'limit',
        dpr: 1,
      });
      return `${optimized} ${width}w`;
    })
    .join(', ');
};

/**
 * Create a blurred placeholder for lazy loading
 */
export const createBlurPlaceholder = (url: string, size: number = 20): string => {
  if (!isCloudinaryUrl(url)) {
    return url;
  }
  
  return optimizeImage(url, {
    width: size,
    quality: 30,
    format: 'auto',
    blur: 1000,
  });
};

/**
 * Get the optimal format for the current browser
 */
export const getBrowserOptimalFormat = (): 'webp' | 'avif' | 'auto' => {
  if (typeof window === 'undefined') return 'auto';
  
  // Check for AVIF support
  const avifSupport = document.createElement('canvas')
    .toDataURL('image/avif')
    .indexOf('data:image/avif') === 0;
  
  if (avifSupport) return 'avif';
  
  // Check for WebP support
  const webpSupport = document.createElement('canvas')
    .toDataURL('image/webp')
    .indexOf('data:image/webp') === 0;
  
  if (webpSupport) return 'webp';
  
  return 'auto';
};

/**
 * React hook for responsive images with device pixel ratio
 */
export const useResponsiveImage = (url: string, preset: ImagePreset = 'gallery') => {
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  const options = getPresetOptions(preset);
  
  return optimizeImage(url, {
    ...options,
    dpr: Math.min(Math.ceil(dpr), 3), // Cap at 3x for performance
  });
};



